/**
 * Service layer — shared record locking
 *
 * Used by both the Gantt scheduler (resourceType 'schedule', resourceId = projectId)
 * and the QC markup tool (resourceType 'qc_version', resourceId = versionId).
 *
 * Pattern:
 *   const lock = await acquireLock('schedule', projectId, userName)
 *   if (!lock.locked) // someone else holds it -> open read-only, show lock.heldBy.name
 *   // while open, call heartbeatLock(...) every few minutes
 *   // on close, releaseLock(...)
 */

async function call(method, body) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/locks', {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'Lock request failed: ' + res.status)
  }
  return res.json()
}

/**
 * Try to acquire (or refresh) a lock on a resource.
 * @returns {Promise<{ locked: boolean, holder?: {id,name}, heldBy?: {id,name}, expiresAt?: string }>}
 *   locked:true  -> you hold it; locked:false -> heldBy names the current holder.
 */
export function acquireLock(resourceType, resourceId, name) {
  return call('POST', { action: 'acquire', resourceType, resourceId, name })
}

/**
 * Push the lock's expiry forward. Call periodically while the editor is open.
 * @returns {Promise<{ locked: boolean, heldBy?: {id,name} }>} locked:false means the lock was lost.
 */
export function heartbeatLock(resourceType, resourceId) {
  return call('POST', { action: 'heartbeat', resourceType, resourceId })
}

/**
 * Force-claim a lock regardless of the current holder (e.g. a stale lock from a
 * prior session). Use only on explicit user action ("Take over editing").
 * @returns {Promise<{ locked: true, holder: {id,name} }>}
 */
export function takeoverLock(resourceType, resourceId, name) {
  return call('POST', { action: 'takeover', resourceType, resourceId, name })
}

/**
 * Release a lock you hold. Safe to call even if you no longer hold it.
 */
export function releaseLock(resourceType, resourceId) {
  return call('DELETE', { resourceType, resourceId })
}
